import { toTitleCase } from '@vrplatform/utils';
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import type { NetRevenueColumnType } from '@vrplatform/ui-common';
import type { ReactNode } from 'react';

const cellBorderColor = '#F3F4F6';
const headerBorderColor = '#F3F4F6';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: '100%',
  },
  rowBorderBottom: {
    borderBottom: `1px solid ${cellBorderColor}`,
    borderColor: cellBorderColor,
  },
  alignCellRight: {
    textAlign: 'right',
  },
  cell: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: 5,
    flexDirection: 'row',
  },
  header: {
    borderBottom: `1px solid ${headerBorderColor}`,
    color: '#707786',
    fontFamily: 'Helvetica-Bold',
  },
});

export type TableColumn = {
  id: string;
  name: string;
  align?: 'right' | 'left';
  hide?: boolean;
  type?: NetRevenueColumnType | undefined;
  value?: string | string[];
};
type Shared = {
  columns: TableColumn[];
};

type TableRowProps = Record<string, number | string>;
type TableProps = {
  rowData: TableRowProps[];
};

export const StatementTable = ({ rowData, ...shared }: TableProps & Shared) => (
  <>
    <TableHeader columns={shared.columns} />
    {rowData.map((row, index, arr) => {
      const isLastRow = index === arr.length - 1;

      return (
        <TableRow
          key={index}
          row={row}
          withBottomBorder={!isLastRow}
          bold={isLastRow}
          {...shared}
        />
      );
    })}
  </>
);

const TableHeader = ({ columns }: { columns: TableColumn[] }) => {
  return (
    <View
      style={{ ...styles.row, ...styles.header }}
      minPresenceAhead={2}
      wrap={false}
    >
      {columns.map((column, index) => {
        const isAmount = column.id === 'amount';
        const alignRight = isAmount || column.align === 'right';

        if (column.hide) return null;

        const isBookingChannelIcon =
          column.type === 'field' &&
          column.value === 'reservation.bookingChannelIcon';

        if (isBookingChannelIcon) {
          return (
            <BookingChannelIconContainer key={index}>
              {column.name}
            </BookingChannelIconContainer>
          );
        }

        return (
          <Text
            key={index}
            style={{
              ...styles.cell,
              ...(alignRight ? styles.alignCellRight : {}),
            }}
          >
            {column.name}
          </Text>
        );
      })}
    </View>
  );
};

const TableRow = ({
  row,
  columns,
  withBottomBorder,
  bold,
}: {
  row: TableRowProps;
  withBottomBorder: boolean;
  bold: boolean;
} & Shared) => {
  const rowStyles = {
    ...styles.row,
    fontFamily: bold ? 'Helvetica-Bold' : undefined,
  };

  return (
    <View
      wrap={false}
      style={
        withBottomBorder
          ? { ...rowStyles, ...styles.rowBorderBottom }
          : rowStyles
      }
    >
      {Object.entries(row).map(([key, value], index) => {
        // hide values that are not in template visible column
        const column = columns.find((i) => i.id === key);
        if (!column || column.hide) return null;

        const isReservationStatus =
          column.type === 'field' && column.value === 'reservation.status';

        const isBookingChannelIcon =
          column.type === 'field' &&
          column.value === 'reservation.bookingChannelIcon';

        if (isReservationStatus) {
          if (!value || typeof value !== 'string') return null;

          return (
            <View style={styles.cell} key={index} wrap={false}>
              <ReservationStatusBadge status={value.toLowerCase()} />
            </View>
          );
        }

        if (isBookingChannelIcon) {
          if (!value || typeof value !== 'string') return null;

          return (
            <BookingChannelIconContainer key={index}>
              <Image
                src={value}
                style={{
                  width: 15,
                  height: 15,
                }}
              />
            </BookingChannelIconContainer>
          );
        }

        return (
          <Text
            key={index}
            wrap={false}
            style={
              column.align === 'right'
                ? { ...styles.cell, ...styles.alignCellRight }
                : styles.cell
            }
          >
            {typeof value === 'number' ? value : value || ' '}
          </Text>
        );
      })}
    </View>
  );
};

const ReservationStatusBadge = ({ status }: { status: string | undefined }) => {
  let color: 'green' | 'yellow' | 'red' = 'yellow';
  const label = toTitleCase(status || '');

  switch (status) {
    case 'booked':
      color = 'green';
      break;
    case 'cancelled':
      color = 'red';
      break;
    case 'inquiry':
      color = 'yellow';
      break;
    default:
      break;
  }

  if (!label) return null;

  return (
    <Text
      style={{
        color,
        padding: 3,
        backgroundColor: 'transparent',
      }}
    >
      {label}
    </Text>
  );
};

const BookingChannelIconContainer = ({ children }: { children: ReactNode }) => (
  <View style={{ ...styles.cell, flexGrow: 0.15 }} wrap={false}>
    {children}
  </View>
);
